import { Heading, Grid, Text, Box, Divider, Icon } from '@chakra-ui/react';
import { Icons } from '@texas/components/shared/Icons';
import {
  garpApi,
  GarpCell,
  GarpCompareData,
} from '@texas/api/endpoints/erp/garpApi';
import { GarpCellFormContext } from '../SetGarpDataModal';
import { MiddleDivider } from '../shared/MiddleDivider';
import { RowDivider } from '../shared/RowDivider';
import {
  CellPhotoRow,
  CellSupplierSizeRow,
  CellSupplierColors,
  CellWeight,
  CellMaterial,
  CellTechnique,
} from './GarpCellRows';
import { useGarpCellForm } from './useGarpCellForm';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { ReactSelectOption } from '@texas/types';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useTranslation } from 'react-i18next';

export function GarpCellCompareView({ data }: { data: GarpCompareData }) {
  const { t } = useTranslation();

  const {
    data: variants,
    refetch: refetchVariants,
    loading: variantsLoading,
  } = useApiResource(articlesApi.getVariants);

  const {
    data: variant,
    refetch: refetchVariant,
    loading: variantLoading,
  } = useApiResource(garpApi.garpCellPaCompareData);

  useEffect(() => {
    refetchVariants(data.articleId);
  }, [data, refetchVariants]);

  useEffect(() => {
    refetchVariant(
      data.mainVariantId ?? data.variantId,
      data.branchId,
      data.matrixNodeXValue!,
      data.matrixNodeYValue!,
    );
  }, [
    data.branchId,
    data.mainVariantId,
    data.matrixNodeXValue,
    data.matrixNodeYValue,
    data.variantId,
    refetchVariant,
  ]);

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        pb={2}
        bg="texas.bg.900"
        _light={{ bg: 'white' }}
      >
        <Grid gridTemplateColumns="40% 20% 40%" alignItems="center">
          <Box>
            <Text fontSize="sm" color="chakra-subtle-text" pb={1}>
              {t('compositionGroup.version.versionData')}
            </Text>
            <Heading>{data.articleNumber}</Heading>
            <Text>
              {data.matrixNodeXName} - {data.matrixNodeYName}
            </Text>
            <Text>{data.versionName}</Text>
            {data.productApproval && (
              <Text>{data.productApproval.paNumber}</Text>
            )}
          </Box>
          <Box display="flex" justifyContent="center">
            <Icon as={Icons.ArrowRight} />
          </Box>
          <Box>
            <Text fontSize="sm" color="chakra-subtle-text" pb={1}>
              {t('garp.garpVariantData')}
            </Text>
            <TexasSelect
              isLoading={variantsLoading}
              onChange={(e) => {
                refetchVariant(
                  e?.value,
                  data.branchId,
                  data.matrixNodeXValue!,
                  data.matrixNodeYValue!,
                );
              }}
              defaultValue={{
                label: `${data.mainVariantName ?? data.variantName} / ${
                  data.branchName
                }`,
                value: data.mainVariantId ?? data.variantId,
              }}
              options={
                variants
                  ?.filter((x) => !x.isProductDevelopment)
                  .map<ReactSelectOption>((x) => ({
                    value: x.id,
                    label: x.name,
                  })) ?? []
              }
              closeMenuOnSelect={true}
            />
          </Box>
        </Grid>
        <Divider mt={2} />
      </Box>
      {variantLoading && <SpinnerWithLabel label={t('general.loadingData')} />}
      {variant && (
        <Form key={variant.variantId} garpCell={variant} data={data} />
      )}
    </>
  );
}

function Form({
  garpCell,
  data,
}: {
  garpCell: GarpCell;
  data: GarpCompareData;
}) {
  const garpForm = useGarpCellForm({
    variantId: garpCell.variantId,
    branchId: garpCell.branchId,
    cellX: data.matrixNodeXValue!,
    cellY: data.matrixNodeYValue!,
    defaultValues: {
      supplierColorCode: garpCell.supplierColorCode,
      supplierSize: garpCell.supplierSize,
      materialId: garpCell.materialId,
      techniqueId: garpCell.techniqueId,
      weight: garpCell.weight,
    },
  });

  return (
    <GarpCellFormContext.Provider value={garpForm}>
      <form {...garpForm.form}>
        <Grid
          gridTemplateColumns="[pa] 40% [mid] 20% [garp] 40% [end]"
          alignItems="center"
          gridRowGap={4}
          py={2}
        >
          <MiddleDivider rows={12} />
          <CellPhotoRow
            variantId={garpCell.variantId}
            folderId={data.folderId}
            cellX={data.matrixNodeXValue!}
            cellY={data.matrixNodeYValue!}
            paFileIdentifier={data.productApproval?.fileIdentifier}
            paFileId={data.productApproval?.fileId}
          />
          <RowDivider rowIndex={2} />
          <CellSupplierSizeRow compositions={data.compositions} />
          <RowDivider rowIndex={4} />
          <CellSupplierColors compositions={data.compositions} />
          <RowDivider rowIndex={6} />
          <CellWeight rowIndex={7} compositions={data.compositions} />
          <RowDivider rowIndex={8} />
          <CellMaterial rowIndex={9} compositions={data.compositions} />
          <RowDivider rowIndex={10} />
          <CellTechnique compositions={data.compositions} />
        </Grid>
      </form>
    </GarpCellFormContext.Provider>
  );
}
